import { generateVerifier, generateChallenge } from '$lib/utils/pkce';

interface AuthState {
  accessToken:  string | null;
  refreshToken: string | null;
  idToken:      string | null;
  expiresAt:    number | null;  // epoch (s)
  loading: boolean;
  color: string
}
export const auth: AuthState = $state({
  accessToken: null,
  refreshToken: null,
  idToken: null,
  expiresAt: null,
  loading: false,
  color: "blue"
});


/* ------------------------------------------------------------ */
/* 1.  Redirect to Shopify login                                */
export async function login() {
  const verifier  = generateVerifier();
  const challenge = await generateChallenge(verifier);
  localStorage.setItem('ca_code_verifier', verifier);

  const url = new URL(import.meta.env.VITE_CA_AUTH_ENDPOINT);
  url.searchParams.set('client_id',   import.meta.env.VITE_CA_CLIENT_ID);
  url.searchParams.set('scope',       import.meta.env.VITE_CA_SCOPE);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('redirect_uri', `${location.origin}/account/callback`);
  url.searchParams.set('state',  crypto.randomUUID());
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  location.href = url.toString();                        // 🚀
}

/* ------------------------------------------------------------ */
/* 2.  Exchange code for tokens                                 */
export async function handleCallback(code: string) {
  auth.loading = true;
  auth.color = "green";
  const body = new URLSearchParams({
    grant_type:    'authorization_code',
    client_id:     import.meta.env.VITE_CA_CLIENT_ID,
    redirect_uri:  `${location.origin}/account/callback`,
    code,
    code_verifier: localStorage.getItem('ca_code_verifier')!,
  });

  const res = await fetch(import.meta.env.VITE_CA_TOKEN_ENDPOINT, {
    method: 'POST',
    headers: {
      'content-type': 'application/x-www-form-urlencoded',
      origin: location.origin,           // ↓ required for some stores
      'user-agent': navigator.userAgent, // ↓                  :contentReference[oaicite:1]{index=1}
    },
    body,
  });
  console.log(res.ok)
  if (!res.ok) throw new Error(await res.text());
  const { access_token, refresh_token, id_token, expires_in } = await res.json();
  console.log("Tokens received:", { access_token, refresh_token, id_token, expires_in });
  if (!access_token || !refresh_token || !id_token || !expires_in) {
    throw new Error('Missing tokens in response');
  }

  
  auth.accessToken  = access_token;
  auth.refreshToken = refresh_token;
  auth.idToken      = id_token;
  auth.expiresAt = Math.floor(Date.now() / 1000) + expires_in;
  localStorage.setItem('ca_access_token',  access_token);
  localStorage.setItem('ca_refresh_token', refresh_token);
  localStorage.setItem('ca_id_token',      id_token);
  localStorage.setItem('ca_expires_at', String(auth.expiresAt));
  
  console.log("Auth state after callback:", $state.snapshot(auth));
  auth.loading = false;
}


/* ------------------------------------------------------------ */
/* 3.  Refresh before expiry (2 h default)                      */
export async function maybeRefresh() {
  if (!auth.refreshToken || !auth.expiresAt) return;
  if (Date.now()/1000 < auth.expiresAt - 60) return;      // >1 min left

  const body = new URLSearchParams({
    grant_type:    'refresh_token',
    client_id:     import.meta.env.VITE_CA_CLIENT_ID,
    refresh_token: auth.refreshToken,
  });
  const res = await fetch(import.meta.env.VITE_CA_TOKEN_ENDPOINT, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body,
  });
  console.log(res.ok)
  if (!res.ok) { logout(); return; }
  const { access_token, refresh_token, expires_in } = await res.json();
  auth.accessToken  = access_token;
  auth.refreshToken = refresh_token;
  auth.expiresAt = Math.floor(Date.now() / 1000) + expires_in;
  localStorage.setItem('ca_access_token',  access_token);
  localStorage.setItem('ca_refresh_token', refresh_token);
  localStorage.setItem('ca_expires_at', String(auth.expiresAt));
  
  console.log("Auth state after callback:", auth);
}

/* ------------------------------------------------------------ */
export function logout() {
  $inspect.trace();
  console.log("LOG OUT TRIGGERED!!");
  localStorage.clear();
  auth.accessToken = auth.refreshToken = auth.idToken = null;
  auth.expiresAt = null;
  location.href = import.meta.env.VITE_CA_LOGOUT_ENDPOINT;
}
